"use client";

import { ImageKit, MegaMenu } from "@aph/ui-lib-humanx";
import NextLink from "next/link";
import React from "react";
import { useSearchParams } from "next/navigation";
import IconProfile from "$/icons/icon-profile.svg";
import routes from "@/helpers/router";
import { NEXT_PUBLIC_CDN_URL, NEXT_PUBLIC_PHARMACY_BASE_URL } from "@/libs/constants/auth";
import { clientRoutes } from "@/helpers/clientRoutes";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import useScrollDirection from "@/redux/hooks/useScrollDirection";
import { ServerSideProps } from "@/redux/interfaces/page";
import { SignedinState } from "@/redux/slice/auth/enums/SignedInState";
import signedInStateSelector from "@/redux/slice/auth/selectors/signedInState";
import { handleOpenLoginModal } from "@/redux/slice/auth/slice";
import { cartItemsSelector } from "@/redux/slice/cart/selectors/cartItems";
import performSignOutUpdates from "@/redux/slice/auth/actions/performSignOutUpdates";

import ProfileSideDrawer from "@/components/ProfileSideDrawer";
import OnboardingStepsModal from "@/components/ProfileSideDrawer/components/OnboardingStepsModal";
import { setIsProfileOpen } from "@/components/ProfileSideDrawer/redux/slice";
import { GACartIconClickedEvent } from "@/libs/trackingHelpers/GACartIconClickedEvent";
import { GALoginButtonClickedEvent } from "@/libs/trackingHelpers/GALoginButtonClickedEvent";
import { buttonClickedEvent } from "@/helpers/TSDEvents";
import SegmentInfoSelector from "@/redux/slice/auth/selectors/userSegmentInfo";
import { NON_ONBOARDED_STATUS, ONBOARDING_STATUS } from "@/helpers/constants";
import RawMenu from "@/components/RawMenu";
import Menu from "@/components/Menu";
import HamburgerSideMenu from "@/components/HamburgerSideMenu";
import styles from "./header.module.scss";

interface HeaderProps {
  pageProps: ServerSideProps;
}

const Header: React.FC<HeaderProps> = (props) => {
  const signedInStatus = useAppSelector(signedInStateSelector);
  const { isLoggedIn, ua, isUserFromApp } = props?.pageProps;
  const dispatch = useAppDispatch();
  const scrollDirection = useScrollDirection();
  const SegmentInfoObject = useAppSelector(SegmentInfoSelector);
  const routerPathname = props?.pageProps?.serverPathname;

  const cartItems = useAppSelector(cartItemsSelector);
  const [isMenuOpen, setIsMenuOpen] = React.useState<boolean>(false);
  const [isHamburgerMenuOpen, setIsHamburgerMenuOpen] = React.useState<boolean>(false);

  const handleLogin: () => void = () => {
    handleTsdEvent({ buttonName: "Login", position: 3 });
    GALoginButtonClickedEvent({ deviceType: ua?.isMobile ? "mobile" : "desktop" });
    dispatch(handleOpenLoginModal(true));
  };

  const handleCartIconClicked = () => {
    GACartIconClickedEvent({ deviceType: ua?.isMobile ? "mobile" : "desktop" });
  };

  const handleTsdEvent = (props: { buttonName: string; position: number }) => {
    const { buttonName, position } = props;
    buttonClickedEvent({
      button_name: buttonName,
      momverse_stage: SegmentInfoObject?.type ? ONBOARDING_STATUS[SegmentInfoObject?.type] : NON_ONBOARDED_STATUS,
      is_loggedin: signedInStatus === SignedinState.SIGNEDIN ? true : false,
      ...(position && { button_position: position }),
    });
  };

  const isPharma = routerPathname?.includes("pharma") || routerPathname?.includes("blogs/article");
  const imageUrl = isPharma
    ? `${NEXT_PUBLIC_CDN_URL}/images/pharmacy_logo.svg`
    : `${NEXT_PUBLIC_CDN_URL}/images/momverse/logo.svg`;
  const searchParams = useSearchParams();
  const logoLink = isPharma ? clientRoutes.pharmaHome() : routes.home;

  // Login CTA for hamburger menu - only show if user is not logged in
  const hamburgerLoginCta =
    signedInStatus === SignedinState.SIGNEDIN || (signedInStatus === SignedinState.UNRESOLVED && isLoggedIn)
      ? null
      : () => {
          handleTsdEvent({ buttonName: "Login", position: 3 });
          GALoginButtonClickedEvent({ deviceType: ua?.isMobile ? "mobile" : "desktop" });
          dispatch(handleOpenLoginModal(true));
        };

  const handleLogout = () => {
    performSignOutUpdates({ dispatch });
  };

  return (
    <>
      <header className={`${styles.header} ${scrollDirection === "down" && ua?.isMobile ? styles.top : ""}`}>
        <div className={`container ${styles.headerRoot}`}>
          {/* For mweb pharma pages, show hamburger icon instead of logo */}
          {ua?.isMobile && isPharma ? (
            <button
              type="button"
              className={`icon-hamberger ${styles.hamburgerBtn}`}
              onClick={() => setIsHamburgerMenuOpen(true)}
              aria-label="Menu"
            />
          ) : (
            <NextLink href={logoLink} prefetch={false}>
              <ImageKit src={imageUrl} alt="logo" height={50} width={70} className={styles.logo} />
            </NextLink>
          )}
          <div className={styles.ctaWrapper}>
            <NextLink
              href={`${NEXT_PUBLIC_PHARMACY_BASE_URL}/medicines-cart`}
              prefetch={false}
              className={`${styles.cartIcon} icon-cart_icon`}
              aria-label="Cart Icon"
              onClick={handleCartIconClicked}
            >
              <span className={styles.cartItemQty}>{cartItems?.length || 0}</span>
            </NextLink>
            {(signedInStatus === SignedinState.UNRESOLVED && isLoggedIn) ||
            signedInStatus === SignedinState.SIGNEDIN ? (
              <IconProfile
                height={24}
                width={24}
                alt="Profile"
                onClick={() => {
                  dispatch(setIsProfileOpen(true));
                }}
                className={styles.profileIcon}
              />
            ) : (
              <button
                type={"button"}
                className={isPharma ? styles.btnLogin_pharma : styles.btnLogin}
                onClick={handleLogin}
              >
                <span>Login</span>
                <IconProfile height={24} width={24} alt="Profile" />
              </button>
            )}
          </div>
          {/* <SearchBar /> */}
        </div>
        {ua?.isDesktop && isPharma && <RawMenu />}
        {ua?.isDesktop && isPharma && props?.pageProps?.categoryData && (
          <MegaMenu categoryData={props?.pageProps?.categoryData} searchParams={searchParams} />
        )}
      </header>
      {ua?.isMobile && !isPharma && <Menu isOpen={isMenuOpen} />}
      {ua?.isMobile && isPharma && (
        <HamburgerSideMenu
          open={isHamburgerMenuOpen}
          handleOpen={setIsHamburgerMenuOpen}
          hamburgerPanelData={props?.pageProps?.hamburgerPanelData}
          loginCta={hamburgerLoginCta}
          logoutCta={handleLogout}
        />
      )}
      <ProfileSideDrawer ua={ua} isLoggedIn={isLoggedIn} isUserFromApp={isUserFromApp} />
      <OnboardingStepsModal />
    </>
  );
};

export default Header;
